import React, { useState, useEffect } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";

interface SettingsGeneralModalProps {
  auth: any;
  db: any;
  currentUser: any;
}

const CountryList: { [key: string]: string } = {
  UNITED_KINGDOM: "United Kingdom",
  GERMANY: "Germany",
  FRANCE: "France",
  SPAIN: "Spain",
};

const LanguageList: { [key: string]: string } = {
  CZECH: "Czech",
  DANISH: "Danish",
  DUTCH: "Dutch",
  ENGLISH_UK: "English (UK)",
  FLEMISH: "Flemish",
  FRENCH: "French",
  GERMAN: "German",
  GREEK: "Greek",
  HINDI: "Hindi",
  INDONESIAN: "Indonesian",
  ITALIAN: "Italian",
  JAPANESE: "Japanese",
  KOREAN: "Korean",
  NORWEGIAN: "Norwegian",
  POLISH: "Polish",
  PORTUGUESE: "Portuguese",
  RUSSIAN: "Russian",
  SPANISH: "Spanish",
  SWEDISH: "Swedish",
  TURKISH: "Turkish",
  UKRAINIAN: "Ukrainian",
};

const Dropdown = ({
  label,
  options,
  selectedValue,
  onChange,
}: {
  label: string;
  options: string[];
  selectedValue: string;
  onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}) => {
  return (
    <div style={{ marginBottom: "2rem" }}>
      <h4>{label}</h4>
      <select
        value={selectedValue}
        onChange={onChange}
        style={{
          display: "block",
          width: "50%",
          padding: "0.5rem",
          fontSize: "1rem",
          borderRadius: "0.5rem",
          border: "1px solid silver",
        }}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

const SettingsGeneralModal = ({
  auth,
  db,
  currentUser,
}: SettingsGeneralModalProps) => {
  const [bio, setBio] = useState("");
  const [responsePreference, setResponsePreference] = useState("");
  const [microphoneAccess, setMicrophoneAccess] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTestingMicrophone, setIsTestingMicrophone] = useState(false);
  const [country, setCountry] = useState(Object.values(CountryList)[0]);
  const [languageInterface, setLanguageInterface] = useState(
    Object.values(LanguageList)[0]
  );
  const [languageTranscript, setLanguageTranscript] = useState(
    LanguageList.ENGLISH_UK // Set default to English (UK)
  );
  const [medicalCodeEnabled, setMedicalCodeEnabled] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userDocRef = doc(db, "users", currentUser.id);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.data();

        if (userData) {
          setCountry(
            CountryList[userData.country] || Object.values(CountryList)[0]
          );
          setLanguageInterface(
            LanguageList[userData.languageInterface] ||
              Object.values(LanguageList)[0]
          );
          setLanguageTranscript(
            LanguageList[userData.languageTranscript] || LanguageList.ENGLISH_UK // Default to English (UK) if not set
          );
          setBio(userData.bio || "");
          setResponsePreference(userData.responsePreference || "");
          setMedicalCodeEnabled(userData.medicalCodeEnabled || false);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, [db, currentUser.id]);

  // UseEffect for microphone check
  useEffect(() => {
    let audioContext: AudioContext | null = null;
    let analyserNode: AnalyserNode | null = null;
    let mediaStream: MediaStream | null = null;

    // Check for microphone access
    if (isTestingMicrophone) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          mediaStream = stream;
          audioContext = new AudioContext();
          analyserNode = audioContext.createAnalyser();
          const microphone = audioContext.createMediaStreamSource(stream);

          analyserNode.smoothingTimeConstant = 0.8;
          analyserNode.fftSize = 1024;

          microphone.connect(analyserNode);

          const dataArray = new Uint8Array(analyserNode.frequencyBinCount);
          const updateAudioLevel = () => {
            if (!analyserNode) return;

            analyserNode.getByteFrequencyData(dataArray);
            const arraySum = dataArray.reduce((a, value) => a + value, 0);
            const average = arraySum / dataArray.length;
            setAudioLevel(average);

            // Call this function on animation frame to keep updating the audio level
            requestAnimationFrame(updateAudioLevel);
          };

          // Start updating the audio level
          requestAnimationFrame(updateAudioLevel);

          setMicrophoneAccess(true);
        })
        .catch((error) => {
          console.error("Error accessing the microphone", error);
          setMicrophoneAccess(false);
        });
    }

    // Cleanup function to stop accessing the microphone
    return () => {
      if (audioContext) {
        audioContext.close();
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
      audioContext = null;
      analyserNode = null;
      mediaStream = null;
    };
  }, [isTestingMicrophone]);

  const getKeyByValue = (object: { [key: string]: string }, value: string) => {
    return Object.keys(object).find((key) => object[key] === value);
  };

  const toggleMedicalCode = async () => {
    const newMedicalCodeEnabled = !medicalCodeEnabled;
    setMedicalCodeEnabled(newMedicalCodeEnabled);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        medicalCodeEnabled: newMedicalCodeEnabled,
      });
    } catch (error) {
      console.error("Error updating medical code setting:", error);
    }
  };

  const handleUpdateBio = async (response: string) => {
    setBio(response);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        bio: response,
      });
    } catch (error) {
      console.error("Error updating bio:", error);
    }
  };

  const handleUpdateResponsePreference = async (response: string) => {
    setResponsePreference(response);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        responsePreference: response,
      });
    } catch (error) {
      console.error("Error updating response preference:", error);
    }
  };

  const handleUpdateCountry = async (selectedCountry: string) => {
    const countryKey = getKeyByValue(CountryList, selectedCountry);
    setCountry(selectedCountry);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        country: countryKey,
      });
    } catch (error) {
      console.error("Error updating country:", error);
    }
  };

  const handleUpdateLanguageInterface = async (selectedLanguage: string) => {
    const languageKey = getKeyByValue(LanguageList, selectedLanguage);
    setLanguageInterface(selectedLanguage);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        languageInterface: languageKey,
      });
    } catch (error) {
      console.error("Error updating interface language:", error);
    }
  };

  const handleUpdateLanguageTranscript = async (selectedLanguage: string) => {
    const languageKey = getKeyByValue(LanguageList, selectedLanguage);
    setLanguageTranscript(selectedLanguage);
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        languageTranscript: languageKey,
      });
    } catch (error) {
      console.error("Error updating transcript language:", error);
    }
  };

  const renderMicrophoneCheck = () => {
    // if (!microphoneAccess) {
    //   return <p>No access to microphone.</p>;
    // }

    const volumeBars = 20; // Total number of bars to display

    const newAudioLevel = Math.round((audioLevel / 100) * volumeBars);

    const volumeMeterBars = Array.from({ length: volumeBars }, (_, i) => {
      const isActive = i < newAudioLevel; // Determines if this bar should be "active" based on the audio level
      const barStyle = {
        height: "2rem",
        width: "0.4rem",
        margin: "0 0.2rem",
        display: "inline-block",
        backgroundColor: isActive ? "var(--color-brand-green)" : "#CCCCCC", // Green for active bars, grey for inactive
        borderRadius: "0.3rem",
      };
      return <div key={i} style={barStyle} />;
    });

    return (
      <div>
        <h4>Microphone Check</h4>
        {isTestingMicrophone ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignContent: "center",
              width: "auto",
            }}
          >
            {/* <div style={{display: "flex", flexDirection: "row", marginBottom:"1rem"}}> */}
            {volumeMeterBars}
            {/* </div> */}
            <button
              className="button-small-brand-green"
              style={{ borderRadius: "2rem", marginLeft: "1rem" }}
              onClick={() => setIsTestingMicrophone(false)}
            >
              Stop
            </button>
          </div>
        ) : (
          <button
            className="button-small-brand-green"
            style={{ borderRadius: "2rem" }}
            onClick={() => setIsTestingMicrophone(true)}
          >
            Test Microphone
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {renderMicrophoneCheck()}
      <div style={{ marginTop: "2rem" }}>
        <Dropdown
          label="Country"
          options={Object.values(CountryList)}
          selectedValue={country}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleUpdateCountry(e.target.value)
          }
        />
      </div>
      {/* <Dropdown
        // label="Interface and Note Language"
        label="Consultation and Note Language"
        options={Object.values(LanguageList)}
        selectedValue={languageInterface}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          handleUpdateLanguageInterface(e.target.value)
        }
      /> */}

      <Dropdown
        label="Consultation and Meeting Language"
        options={Object.values(LanguageList)}
        selectedValue={languageTranscript}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          handleUpdateLanguageTranscript(e.target.value)
        }
      />
      {/* <div style={{ display: "flex", flexDirection:"column", width:"50%" }}>
          <div style={{  fontSize: "1rem", fontWeight: "bold", textAlign: "left" }}>Motics Copilot Response Customisation</div>
          <div style={{fontSize:"0.8rem", marginBottom:"1rem"}}>Customise the Motics Copilot's responses by providing your personal information and defining your preferred response style.</div>
          <label htmlFor="bio">About yourself</label>
          <textarea
          style={{ borderRadius: "0.5rem", marginBottom: "1rem", border: "1px solid silver", resize:"none"}}
            id="bio"
            value={bio}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleUpdateBio(event.target.value)}
            rows={8}
            placeholder="My name is Adam Smith. I am a physiotherapist in London, United Kingdom."
          />
          <label htmlFor="responsePreference">
            Response instructions and preferences
          </label>
          <textarea
          style={{ borderRadius: "0.5rem", marginBottom: "1rem", border: "1px solid silver", resize:"none"}}
            id="responsePreference"
            value={responsePreference}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleUpdateResponsePreference(event.target.value)}
            rows={8}
            placeholder="* Use medical shorthand and acronyms whenever possible.
            * If there is insufficient information, you must omit either that section, or the entire notes."
          />
        </div> */}
      <h4>Medical Code [BETA]</h4>
      <div style={{width:"100%"}}>
        <label
          className="switch"
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", width:"100%" }}
        >
          Add ICD-10 diagnostic and procedure codes to generated notes
          <input
            type="checkbox"
            style={{ width:"fit-content"}}
            checked={medicalCodeEnabled}
            onChange={toggleMedicalCode}
          />
        </label>
      </div>

      {/* Add a toggle switch to add medical code to notes */}
    </>
  );
};

export default SettingsGeneralModal;
